import { useState } from 'react';
import { Star } from 'lucide-react';
import api from '../../services/api';

interface Props {
  productId: number;
  /** Called after a successful submit so the parent can refetch the list. */
  onSubmitted?: () => void;
  className?: string;
}

/**
 * Minimal "Write a review" form. Posts to the public storefront endpoint
 * `POST /store/products/:productId/reviews`. Server-side validation +
 * 24h dedupe per email + verified-buyer auto-flag. Keeps frontend dumb.
 *
 * Photos are a real file upload: the file posts to
 * `POST /store/products/:productId/reviews/photo` on select, and the URL that
 * comes back rides along in the review body. It used to be a paste-a-URL
 * field, which a shopper holding a photo on their phone could not satisfy.
 */
export function SubmitReviewForm({ productId, onSubmitted, className = '' }: Props) {
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [authorName, setAuthorName] = useState('');
  const [authorEmail, setAuthorEmail] = useState('');
  const [text, setText] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      await api.post(`/store/products/${productId}/reviews`, {
        rating,
        authorName: authorName.trim(),
        authorEmail: authorEmail.trim() || undefined,
        text: text.trim(),
        imageUrl: imageUrl.trim() || undefined,
      });
      setAuthorName('');
      setAuthorEmail('');
      setText('');
      setImageUrl('');
      setRating(5);
      setDone(true);
      onSubmitted?.();
    } catch (e: any) {
      const msg = e?.response?.data?.message;
      setErr(
        Array.isArray(msg) ? msg.join('. ') : msg ?? 'Could not submit review. Try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div
        className={`rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 ${className}`}
        role="status"
      >
        Thanks — your review is live. It may take a moment to appear above.
        <button
          type="button"
          onClick={() => setDone(false)}
          className="ml-2 font-semibold underline-offset-4 hover:underline"
        >
          Write another
        </button>
      </div>
    );
  }

  const displayRating = hoverRating || rating;

  return (
    <form
      onSubmit={submit}
      className={`grid gap-3 rounded-2xl border p-4 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_rgba(0,0,0,0.06)] ${className}`}
      style={{
        background: 'color-mix(in srgb, currentColor 4%, transparent)',
        borderColor: 'color-mix(in srgb, currentColor 14%, transparent)',
        color: 'inherit',
      }}
      aria-label="Write a review"
    >
      <h3 className="text-base font-semibold">Write a review</h3>

      <fieldset className="flex items-center gap-2" aria-label="Rating">
        <span className="text-sm" style={{ opacity: 0.7 }}>Rating</span>
        <div className="flex items-center gap-0.5" onMouseLeave={() => setHoverRating(0)}>
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onMouseEnter={() => setHoverRating(n)}
              onClick={() => setRating(n)}
              className="p-1 -m-1"
              aria-label={`${n} star${n === 1 ? '' : 's'}`}
            >
              <Star
                className={`w-5 h-5 transition-colors ${
                  n <= displayRating ? 'fill-amber-500 text-amber-500' : 'text-neutral-300'
                }`}
              />
            </button>
          ))}
        </div>
      </fieldset>

      <input
        type="text"
        required
        minLength={2}
        maxLength={80}
        value={authorName}
        onChange={(e) => setAuthorName(e.target.value)}
        placeholder="Your name"
        className="rounded-xl border px-3 py-2.5 text-sm focus:outline-none focus:ring-2"
        style={{
          background: 'color-mix(in srgb, currentColor 6%, transparent)',
          borderColor: 'color-mix(in srgb, currentColor 14%, transparent)',
          color: 'inherit',
        }}
      />

      <input
        type="email"
        value={authorEmail}
        onChange={(e) => setAuthorEmail(e.target.value)}
        placeholder="Email (optional — used to mark verified buyer)"
        className="rounded-xl border px-3 py-2.5 text-sm focus:outline-none focus:ring-2"
        style={{
          background: 'color-mix(in srgb, currentColor 6%, transparent)',
          borderColor: 'color-mix(in srgb, currentColor 14%, transparent)',
          color: 'inherit',
        }}
      />

      <textarea
        required
        minLength={10}
        maxLength={1500}
        rows={4}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="What did you think?"
        className="rounded-xl border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 resize-y"
        style={{
          background: 'color-mix(in srgb, currentColor 6%, transparent)',
          borderColor: 'color-mix(in srgb, currentColor 14%, transparent)',
          color: 'inherit',
        }}
      />

      {/* Photo. This was a paste-a-URL field, which a shopper holding a photo
          on their phone has no way to satisfy — so nobody attached one. Real
          file picker now; the upload happens on select so the URL is already
          in hand by the time they submit. */}
      <div className="flex flex-col gap-2">
        {imageUrl ? (
          <div className="flex items-center gap-3">
            <img
              src={imageUrl}
              alt="Your photo"
              className="w-16 h-16 rounded-xl object-cover"
              style={{ background: 'color-mix(in srgb, currentColor 6%, transparent)' }}
            />
            <button
              type="button"
              onClick={() => setImageUrl('')}
              className="text-sm underline underline-offset-2"
              style={{ opacity: 0.75 }}
            >
              Remove photo
            </button>
          </div>
        ) : (
          <label
            className="rounded-xl border px-3 py-2.5 text-sm cursor-pointer inline-flex items-center gap-2"
            style={{
              background: 'color-mix(in srgb, currentColor 6%, transparent)',
              borderColor: 'color-mix(in srgb, currentColor 14%, transparent)',
              color: 'inherit',
            }}
          >
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="sr-only"
              disabled={uploading}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                e.target.value = ''; // let the same file be re-picked after an error
                if (!file) return;
                if (file.size > 5 * 1024 * 1024) {
                  setErr('Photo must be 5MB or smaller.');
                  return;
                }
                setErr(null);
                setUploading(true);
                try {
                  const body = new FormData();
                  body.append('file', file);
                  const res = await api.post(
                    `/store/products/${productId}/reviews/photo`,
                    body,
                  );
                  setImageUrl(res.data?.url || '');
                } catch (ex: any) {
                  const msg = ex?.response?.data?.message;
                  setErr(
                    Array.isArray(msg) ? msg.join('. ') : msg ?? 'Could not upload that photo.',
                  );
                } finally {
                  setUploading(false);
                }
              }}
            />
            <span style={{ opacity: 0.75 }}>
              {uploading ? 'Uploading photo…' : 'Add a photo (optional)'}
            </span>
          </label>
        )}
      </div>

      {err && (
        <p role="alert" className="text-sm text-red-700">
          {err}
        </p>
      )}

      <button
        type="submit"
        disabled={busy || uploading}
        className="self-start inline-flex items-center justify-center px-5 py-3 rounded-full font-semibold text-white shadow-md hover:shadow-lg active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--primary-color)]"
        style={{ background: 'var(--primary-color, #f97316)' }}
      >
        {busy ? 'Submitting…' : 'Submit review'}
      </button>
    </form>
  );
}
